/* ============================================================================
   ModalAtividades / styles.js
   Modal de atividades e notas do aluno (portal do responsável).
   Segue a identidade do boletim: faixa azul (#007bff), bordas roxas (#a06cf0).
   Arquivo AUTOSSUFICIENTE (importa styled/keyframes).
   ========================================================================== */

   import styled, { keyframes } from 'styled-components'

   const fadeIn = keyframes`
     from { opacity: 0; transform: translateY(10px); }
     to   { opacity: 1; transform: translateY(0); }
   `
   
   /* ---------- overlay + caixa -------------------------------------------------- */
   export const Overlay = styled.div`
     position: fixed;
     inset: 0;
     z-index: 1000;
     display: flex;
     align-items: flex-start;
     justify-content: center;
     padding: 24px 16px;
     overflow-y: auto;
     background: rgba(20, 10, 40, 0.5);
   
     @media (max-width: 768px) { padding: 10px; }
   
     @media print {
       position: static;
       background: none;
       padding: 0;
       overflow: visible;
     }
   `
   
   export const ModalCard = styled.div`
     width: 100%;
     max-width: 900px;
     background: var(--surface);
     border-radius: 12px;
     box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
     /* NÃO usar overflow: hidden — ele cria um contexto de recorte que anula o
        position:sticky do BimHeader. O arredondamento é mantido pelo border-radius. */
     animation: ${fadeIn} 0.25s ease both;
   
     @media print {
       max-width: 100%;
       box-shadow: none;
       border-radius: 0;
       animation: none;
     }
   `
   
   /* ---------- topo ------------------------------------------------------------- */
   export const TopBar = styled.div`
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 10px;
     padding: 12px 16px;
     border-bottom: 1px solid var(--line);
   
     @media print { display: none; }
   `
   
   export const CloseButton = styled.button`
     display: inline-flex;
     align-items: center;
     justify-content: center;
     width: 34px;
     height: 34px;
     border: 1px solid var(--line);
     border-radius: 8px;
     background: var(--surface);
     color: var(--text, var(--text-muted));
     cursor: pointer;
     transition: background 0.15s;
   
     &:hover { background: var(--surface); }
   `
   
   export const PrintButton = styled.button`
     display: inline-flex;
     align-items: center;
     gap: 6px;
     color: #fff;
     background-color: #4caf50;
     border: none;
     border-radius: 6px;
     padding: 9px 18px;
     font-size: 14px;
     font-weight: 600;
     cursor: pointer;
   
     &:hover { background-color: #45a049; }
   `
   
   /* ---------- corpo ------------------------------------------------------------ */
   export const Body = styled.div`
     padding: 16px 24px 24px;
     text-align: center;
   
     @media (max-width: 768px) { padding: 12px; }
   `
   
   export const Loading = styled.div`
     padding: 48px 0;
     text-align: center;
     color: var(--text, var(--text-muted));
   `
   
   export const Empty = styled.div`
     padding: 44px 16px;
     text-align: center;
     color: var(--text, var(--text-muted));
     font-size: 15px;
   `
   
   /* ---------- título + identificação ------------------------------------------ */
   export const ContLogo = styled.div`
     position: relative;
     display: flex;
     justify-content: center;
     align-items: center;
     width: 100%;
     padding-bottom: 16px;
   
     h2 { margin: 0; font-size: 1.9em; color: var(--text, var(--text-muted)); }
   
     @media (max-width: 600px) {
       padding-bottom: 10px;
       h2 { font-size: 1.35em; }
     }
   `
   
   export const Preview = styled.img`
     position: absolute;
     left: 0;
     width: 100px;
     height: 100px;
     object-fit: contain;
   
     @media (max-width: 600px) { width: 58px; height: 58px; }
   `
   
   export const YearBar = styled.div`
     background-color: #007bff;
     color: #fff;
     padding: 6px;
     border-radius: 8px;
   
     h3 { margin: 0; font-size: 1.3em; }
   `
   
   export const InfoBox = styled.div`
     display: flex;
     flex-direction: column;
     align-items: flex-start;
     text-align: left;
     gap: 3px;
     padding: 12px 6px;
     width: 100%;
   
     span { font-size: 13.5px; color: var(--text, var(--text-muted)); }
     strong { color: #a06cf0; margin-right: 4px; }
   
     @media (max-width: 768px) { font-size: 12.5px; }
   `
   
   /* ---------- quadro de cada bimestre ----------------------------------------- */
   /* IMPORTANTE: sem `overflow: hidden` aqui — ele quebraria o position:sticky
      do cabeçalho. Os cantos arredondados ficam no próprio header e no último filho. */
   export const BimBlock = styled.section`
     width: 100%;
     margin-top: 14px;
     border: 1px solid #a06cf0;
     border-radius: 10px;
     background: var(--surface);
   
     /* arredonda o último bloco de matéria, já que o pai não corta mais */
     & > div:last-child { border-radius: 0 0 9px 9px; overflow: hidden; }
   `
   
   /* Cabeçalho do período — GRUDA no topo enquanto o usuário rola dentro daquele
      bimestre. Ao chegar no período seguinte, o novo header empurra e assume o topo,
      então o usuário sempre sabe em qual período está. */
   export const BimHeader = styled.header`
     position: sticky;
     top: 0;
     z-index: 5;
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 10px;
     padding: 8px 14px;
     background: #a06cf0;
     color: #fff;
     border-radius: 9px 9px 0 0;
     box-shadow: 0 2px 8px rgba(106, 13, 173, 0.28);
   
     h4 { margin: 0; font-size: 1.05em; font-weight: 700; }
   
     @media (max-width: 768px) {
       flex-direction: column;
       align-items: flex-start;
       gap: 4px;
       padding: 8px 10px;
     }
   
     @media print {
       position: static;
       box-shadow: none;
     }
   `
   
   export const BimTotals = styled.span`
     font-size: 0.85em;
     font-weight: 600;
     opacity: 0.95;
     white-space: nowrap;
   `
   
   /* ---------- bloco de cada matéria dentro do bimestre ------------------------ */
   export const MatterBlock = styled.div`
     border-top: 1px solid var(--line);
   
     &:first-child { border-top: none; }
   `
   
   export const MatterTitle = styled.div`
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 10px;
     padding: 8px 14px;
     background: #f7f4fb;
     font-weight: 700;
     color: var(--text, var(--text-muted));
     font-size: 0.95em;
     text-align: left;
   
     @media (max-width: 768px) { padding: 8px 10px; font-size: 0.9em; }
   `
   
   export const MatterSum = styled.span`
     font-size: 0.9em;
     font-weight: 700;
     color: #a06cf0;
     white-space: nowrap;
   `
   
   /* ---------- tabela de atividades -------------------------------------------- */
   export const ActTable = styled.div`
     display: grid;
     grid-template-columns: 1fr auto auto;
     width: 100%;
   
     @media (max-width: 600px) {
       grid-template-columns: 1fr auto auto;
       font-size: 0.88em;
     }
   `
   
   export const ActHead = styled.div`
     padding: 6px 14px;
     background: var(--surface);
     font-size: 0.78em;
     font-weight: 700;
     text-transform: uppercase;
     letter-spacing: 0.4px;
     color: var(--text, var(--text-muted));
     border-bottom: 1px solid var(--line);
     text-align: ${({ $align }) => $align || 'left'};
     white-space: nowrap;
   
     @media (max-width: 600px) { padding: 6px 8px; }
   `
   
   export const ActCell = styled.div`
     padding: 9px 14px;
     border-bottom: 1px solid #f0f0f0;
     text-align: ${({ $align }) => $align || 'left'};
     color: var(--text, var(--text-muted));
     font-size: 0.95em;
     display: flex;
     align-items: center;
     ${({ $align }) => $align === 'center' && 'justify-content: center;'}
     ${({ $align }) => $align === 'right' && 'justify-content: flex-end;'}
   
     @media (max-width: 600px) { padding: 8px; }
   `
   
   export const ActName = styled(ActCell)`
     flex-direction: column;
     align-items: flex-start;
     gap: 2px;
   `
   
   export const ActType = styled.span`
     font-size: 0.78em;
     color: #888;
     text-transform: uppercase;
     letter-spacing: 0.3px;
   `
   
   /* nota do aluno na atividade — colorida conforme o valor da atividade:
      >= 90% do valor → verde · < 50% → vermelho · caso contrário → azul */
   export const ActGrade = styled.span`
     display: inline-flex;
     align-items: center;
     justify-content: center;
     min-width: 46px;
     padding: 3px 8px;
     border-radius: 6px;
     font-weight: 800;
     ${({ $grade, $valor }) => {
       const g = parseFloat(String($grade).replace(',', '.'))
       const v = parseFloat(String($valor).replace(',', '.'))
       if (isNaN(g)) return 'color: #999;'
       if (!isNaN(v) && v > 0) {
         if (g >= 0.9 * v) return 'color: var(--green); background: rgba(29,127,20,.10);'
         if (g < 0.5 * v) return 'color: red; background: rgba(255,0,0,.08);'
         return 'color: blue; background: rgba(0,0,255,.07);'
       }
       return 'color: var(--text, var(--text-muted));'
     }}
   `
   
   export const ActValue = styled.span`
     color: var(--text, var(--text-muted));
     font-weight: 600;
     white-space: nowrap;
   `
   
   export const NoActivities = styled.div`
     padding: 14px;
     color: #888;
     font-size: 0.9em;
     text-align: center;
   `